import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useMatter } from '@/hooks/useMatter';
import { type UpdatePoliceReport2 } from '@/services/api/generated/policeReportsService';
import {
  usePoliceReportsPartialUpdateMutation,
  usePoliceReportsRetrieveQuery,
} from '@/services/api/police';

import { PoliceReportForm } from './forms/PoliceReportForm';

export const EditPoliceReportPage = () => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const { police_report_id } = useParams<{ police_report_id: string }>();
  const [updateReport, { isLoading: isUpdating }] =
    usePoliceReportsPartialUpdateMutation();

  const {
    data: report,
    isLoading,
    error,
  } = usePoliceReportsRetrieveQuery(
    {
      matterId,
      id: police_report_id || '',
    },
    { skip: !police_report_id },
  );

  const [formData, setFormData] = useState<Partial<UpdatePoliceReport2>>({});

  useEffect(() => {
    if (report) {
      setFormData({
        id: report.id,
        attestation_status: report.attestation_status,
        attestation_description: report.attestation_description,
        description: report.description,
        report_identifier: report.report_identifier,
        reported_incident_date: report.reported_incident_date,
        reported_location: report.reported_location,
        involved_officers: report.involved_officers,
        police_department_id: report.police_department.id,
      });
    }
  }, [report]);

  const handleBack = () => {
    navigate(`/matters/${matterId}/police/${police_report_id}`);
  };

  const handleSubmit = async (data: Partial<UpdatePoliceReport2>) => {
    if (!police_report_id) return;

    try {
      const updateData = {
        ...data,
        id: police_report_id,
        involved_officers: data.involved_officers,
      };

      await updateReport({
        matterId,
        id: police_report_id,
        patchedUpdatePoliceReport: updateData,
      }).unwrap();

      toast.success('Police report updated successfully');
      navigate(`/matters/${matterId}/police/${police_report_id}`);
    } catch (err) {
      toast.error('Failed to update police report');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading police report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load police report</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="flex items-center space-x-1"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span>Back to Report</span>
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Police Report
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg">
          <div className="p-6">
            <PoliceReportForm
              initialData={formData}
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isLoading={isUpdating}
              submitButtonText="Update Report"
              isEditing
            />
          </div>
        </div>
      </div>
    </div>
  );
};
